import { Button, Skeleton } from '@parcel/ui';
import { useQuery } from '@tanstack/react-query';
import { Minus, Plus } from 'lucide-react';
import * as React from 'react';
import { formatSpecMoney, getSpecBilling } from '../../lib/spec-billing.js';
import { isSpecSurfaceVisible } from '../../lib/spec-config.js';
import { useSpecLaunchState } from '../../lib/spec-launch.js';

/**
 * AI seats inside `ManageSeatsDialog`, so one control buys both seat kinds.
 *
 * The two are genuinely different purchases -- workspace seats are base-plan
 * access, AI seats are $20 a month each and are what produce the chat
 * allowance -- but a manager adding people thinks about them together, and
 * splitting them across two pages is what made the old Billing page sprawl.
 *
 * CONTROLLED, not self-saving. The dialog owns the target and commits it,
 * because two save buttons in one dialog is the shape we are deleting. What
 * stays independent is the OUTCOME: the dialog writes each seat kind
 * separately and reports each separately, so a Stripe failure on one cannot
 * discard a valid change to the other.
 *
 * No cost preview, deliberately. A workspace seat's price lives in Stripe and
 * has to be fetched, which is why that half can fail on a preview outage. An AI
 * seat's price is on the summary this section already reads, so it states its
 * own rate without a round trip and stays usable when the other half is down.
 */

export interface SpecSeatQuantitySectionProps {
  workspaceId: string;
  /** Target count, owned by the dialog. Null until the summary lands. */
  target: number | null;
  onTargetChange: (target: number) => void;
  /**
   * Reports the count the dialog should treat as "unchanged", and the assigned
   * count so it can refuse an impossible decrease.
   *
   * `quantity`, not `purchased`: with a period-end reduction queued these are
   * different numbers, and the one a change has to differ from is the pending
   * one. See the baseline comment in the body.
   */
  onBaselineLoaded: (baseline: { quantity: number; assigned: number }) => void;
  disabled: boolean;
}

/** Mirrors the server's own ceiling (`MAX_SPEC_SEAT_QUANTITY`). */
const MAX_AI_SEATS = 1000;

function SpecSeatQuantitySectionContent({
  workspaceId,
  target,
  onTargetChange,
  onBaselineLoaded,
  disabled,
}: SpecSeatQuantitySectionProps) {
  const billingQuery = useQuery({
    queryKey: ['spec-billing', workspaceId],
    queryFn: () => getSpecBilling(workspaceId),
  });
  const summary = billingQuery.data ?? null;

  const purchased = summary?.purchasedSeatQuantity ?? 0;
  const assigned = summary?.assignedSeatCount ?? 0;
  /**
   * The count this stepper starts from, and the one "no change" is measured
   * against. The PENDING quantity when a period-end reduction is queued,
   * because that is the count the workspace is heading to.
   *
   * Baselining on `purchased` instead made the reduction impossible to cancel.
   * The server cancels one by re-submitting the purchased quantity, which
   * supersedes the older intent at the period boundary (spec-usage.ts) -- but
   * the dialog seeded target and baseline both to `purchased`, so that exact
   * write always read as "no change" and never left the browser. The queued
   * reduction could be created and never undone.
   */
  const baseline = summary?.pendingSeatQuantity ?? purchased;
  const pendingDiffers = summary?.pendingSeatQuantity != null && baseline !== purchased;

  React.useEffect(() => {
    if (summary) onBaselineLoaded({ quantity: baseline, assigned });
  }, [summary, baseline, assigned, onBaselineLoaded]);

  if (!summary) {
    // A failure says what happened; loading holds the stepper's shape so the row does not pop in.
    return billingQuery.error ? (
      <div className="rounded-md border px-4 py-3" data-testid="ai-seat-quantity-error">
        <p className="text-sm font-medium">AI seats</p>
        <p className="text-xs text-muted-foreground">
          AI seat details are unavailable right now. Close this and try again in a few minutes.
        </p>
      </div>
    ) : (
      <div
        className="flex items-center justify-between rounded-md border px-4 py-3"
        data-testid="ai-seat-quantity-skeleton"
      >
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-3 w-24" />
        </div>
        <Skeleton className="h-8 w-28" />
      </div>
    );
  }

  // Free cannot buy AI seats at all, and the seat routes refuse it. Saying so
  // is more use than a stepper that can only be rejected.
  if (summary.eligibility === 'free_grant_only') {
    return (
      <div className="rounded-md border px-4 py-3" data-testid="ai-seat-quantity-ineligible">
        <p className="text-sm font-medium">AI seats</p>
        <p className="text-xs text-muted-foreground">Available on Starter and Pro.</p>
      </div>
    );
  }

  const value = target ?? baseline;
  // Unassigning happens on Members, one person at a time, so this stops at the
  // assigned count rather than letting a manager commit a change the server
  // can only refuse with `spec_assignment_limit`.
  const floor = assigned;
  const atFloor = value <= floor;

  return (
    <div className="space-y-2" data-testid="ai-seat-quantity">
      <div className="flex items-center justify-between rounded-md border px-4 py-3">
        <div>
          <p className="text-sm font-medium">AI seats</p>
          {/*
            The count, phrased exactly like the Extra seats row above it, so the
            two read as one control rather than two designs. What a seat buys is
            not stated here: the per-seat credit figure is a pricing detail this
            dialog is not the place to publish, and the price itself moved to
            the line below, where it appears only once there is a charge to
            attach it to.
          */}
          <p className="text-xs text-muted-foreground">{value} seats total</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={disabled || atFloor}
            onClick={() => onTargetChange(Math.max(floor, value - 1))}
            aria-label="Remove an AI seat"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="w-6 text-center text-base font-medium tabular-nums">{value}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            disabled={disabled || value >= MAX_AI_SEATS}
            onClick={() => onTargetChange(Math.min(MAX_AI_SEATS, value + 1))}
            aria-label="Add an AI seat"
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {atFloor && assigned > 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="ai-seat-quantity-floor">
          {assigned} assigned. Turn a member's AI seat off on the Members page before removing it
          here.
        </p>
      ) : null}
      {/*
        Why the stepper reads lower than what is being paid for, and how to undo
        it. Shown only until something is staged, so this and the line below
        never both describe the same click.
      */}
      {pendingDiffers && value === baseline ? (
        <p className="text-xs text-muted-foreground" data-testid="ai-seat-quantity-pending">
          Reducing from {purchased} at the end of the billing period. Set it back to {purchased} to
          cancel.
        </p>
      ) : null}
      {/*
        Measured against the BASELINE, not against `purchased`: with a reduction
        queued those differ, and returning to `purchased` is a real change --
        the one that cancels it -- which a comparison against `purchased` would
        have called no change and said nothing about.

        Also the only place an AI seat's price appears, and the only place it
        needs to: a rate rather than a total, because the actual charge is
        prorated from today to the period end and only Stripe knows that
        figure. Stating a computed total here would be a number this component
        cannot stand behind.
      */}
      {value !== baseline ? (
        <p className="text-xs text-muted-foreground">
          {value > purchased
            ? `Adding a seat charges ${formatSpecMoney(summary.seatPriceCents)} per month, prorated from today.`
            : value === purchased
              ? 'Cancels the reduction scheduled for the end of the billing period.'
              : 'Removing a seat takes effect at the end of the billing period.'}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The launch guard. Owns one hook and returns null above every hook the body
 * owns, so a workspace without Spec never issues the Spec billing read and a
 * mid-session flip mounts or unmounts the whole subtree.
 */
export function SpecSeatQuantitySection(props: SpecSeatQuantitySectionProps) {
  const launchState = useSpecLaunchState();
  if (!isSpecSurfaceVisible(launchState)) return null;
  // `read_only` keeps Spec surfaces VISIBLE and refuses every mutation with
  // 409 `spec_read_only`, so a live stepper here could only ever produce a
  // rejected write. Pre-disabling it is the same call `spec-billing-card.tsx`
  // already makes for its own write controls.
  return (
    <SpecSeatQuantitySectionContent {...props} disabled={props.disabled || launchState !== 'on'} />
  );
}
